import { z } from "zod/v4";

/** Caps to keep a stored doc bounded. */
const MAX_TEXT_LEN = 20000;
const MAX_NODES = 2000;
const MAX_DEPTH = 8;

/** Only http/https/mailto links are allowed anywhere a link mark appears. */
export function isSafeHref(href: string): boolean {
  const s = href.trim().toLowerCase();
  return s.startsWith("http://") || s.startsWith("https://") || s.startsWith("mailto:");
}

/* ------------------------------------------------------------------------
 * Explicit recursive TypeScript shape (kept accurate and hand-written since
 * the runtime schema below is intentionally shallow — see the validation
 * note further down).
 * ---------------------------------------------------------------------- */

interface BoldMark {
  type: "bold";
}
interface ItalicMark {
  type: "italic";
}
interface LinkMark {
  type: "link";
  attrs: { href: string; [key: string]: unknown };
}
type TextMark = BoldMark | ItalicMark | LinkMark;

interface TextNode {
  type: "text";
  text: string;
  marks?: TextMark[];
}

interface Paragraph {
  type: "paragraph";
  content?: TextNode[];
}

interface Heading {
  type: "heading";
  attrs: { level: 2 | 3 | 4 };
  content?: TextNode[];
}

// listItem may hold paragraphs and (nested) lists; lists hold listItems.
interface ListItem {
  type: "listItem";
  content: Array<Paragraph | BulletList | OrderedList>;
}
interface BulletList {
  type: "bulletList";
  content: ListItem[];
}
interface OrderedList {
  type: "orderedList";
  attrs?: Record<string, unknown>;
  content: ListItem[];
}

type BlockNode = Paragraph | Heading | BulletList | OrderedList;

/** ProseMirror/Tiptap document restricted to our node/mark set. */
export interface RichTextDoc {
  type: "doc";
  content: BlockNode[];
}

/* ------------------------------------------------------------------------
 * Runtime validation.
 *
 * IMPORTANT: this does NOT use recursive z.lazy descent. A recursive Zod
 * schema parses the entire input depth-first *before* any superRefine can
 * run, so a maliciously (or accidentally) deep document — a few hundred
 * levels of nested lists is a trivially small JSON payload — blows the JS
 * call stack with a RangeError, and `safeParse` is supposed to never throw.
 *
 * Instead the Zod schema itself is shallow (just `{ type: "doc", content:
 * unknown[] }`) and the entire structural/mark/bounds validation happens in
 * one `superRefine` pass using an explicit work stack (no recursion). This
 * makes it impossible to overflow the stack: an over-deep or over-large
 * document simply produces a normal validation issue.
 * ---------------------------------------------------------------------- */

type NodeRole = "topBlock" | "listItemChild" | "listContainerChild" | "inlineChild";

/** Which node `type`s are structurally allowed in each position. */
const ROLE_ALLOWED_TYPES: Record<NodeRole, ReadonlySet<string>> = {
  topBlock: new Set(["paragraph", "heading", "bulletList", "orderedList"]),
  listItemChild: new Set(["paragraph", "bulletList", "orderedList"]),
  listContainerChild: new Set(["listItem"]),
  inlineChild: new Set(["text"]),
};

const KNOWN_NODE_TYPES = new Set([
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "listItem",
  "text",
]);

const KNOWN_MARK_TYPES = new Set(["bold", "italic", "link"]);

/** Nodes whose `content` array must hold at least one child. */
const REQUIRES_NON_EMPTY_CONTENT = new Set(["bulletList", "orderedList", "listItem"]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

interface WorkItem {
  node: unknown;
  depth: number;
  role: NodeRole;
}

const richTextDocShape = z.object({
  type: z.literal("doc"),
  content: z.array(z.unknown()),
});

export const richTextDocSchema = richTextDocShape.superRefine((docVal, ctx) => {
  let nodeCount = 1; // count the root `doc` node itself, matching prior behavior.
  let textLen = 0;
  let depthIssued = false;
  let nodesIssued = false;

  const stack: WorkItem[] = docVal.content.map((node) => ({ node, depth: 1, role: "topBlock" }));

  while (stack.length > 0) {
    // Bail out once we already know the document is oversized: further work
    // cannot change the (already-failed) outcome.
    if (nodeCount > MAX_NODES) {
      if (!nodesIssued) {
        ctx.addIssue({ code: "custom", message: "Document trop volumineux." });
        nodesIssued = true;
      }
      break;
    }

    const item = stack.pop();
    if (!item) break;
    const { node, depth, role } = item;
    nodeCount += 1;

    if (depth > MAX_DEPTH) {
      if (!depthIssued) {
        ctx.addIssue({ code: "custom", message: "Structure trop imbriquée." });
        depthIssued = true;
      }
      // Don't descend further down this branch — this is what keeps the
      // walk bounded even for a document nested hundreds of levels deep.
      continue;
    }

    if (!isPlainObject(node) || typeof node.type !== "string") {
      ctx.addIssue({ code: "custom", message: "Nœud invalide." });
      continue;
    }

    const type = node.type;

    if (!KNOWN_NODE_TYPES.has(type)) {
      ctx.addIssue({ code: "custom", message: `Type de nœud inconnu : ${type}.` });
      continue;
    }

    if (!ROLE_ALLOWED_TYPES[role].has(type)) {
      ctx.addIssue({ code: "custom", message: `Nœud "${type}" non autorisé à cet emplacement.` });
      continue;
    }

    if (type === "text") {
      const text = node.text;
      if (typeof text !== "string" || text.length === 0) {
        ctx.addIssue({ code: "custom", message: "Texte vide." });
      } else {
        textLen += text.length;
      }

      const marks = node.marks;
      if (marks !== undefined) {
        if (!Array.isArray(marks)) {
          ctx.addIssue({ code: "custom", message: "Marques invalides." });
        } else {
          for (const mark of marks) {
            if (!isPlainObject(mark) || typeof mark.type !== "string" || !KNOWN_MARK_TYPES.has(mark.type)) {
              ctx.addIssue({ code: "custom", message: "Marque inconnue." });
              continue;
            }
            if (mark.type === "link") {
              const attrs = mark.attrs;
              const href = isPlainObject(attrs) ? attrs.href : undefined;
              if (typeof href !== "string" || !isSafeHref(href)) {
                ctx.addIssue({ code: "custom", message: "Lien non autorisé." });
              }
            }
          }
        }
      }
      continue;
    }

    if (type === "heading") {
      const attrs = node.attrs;
      const level = isPlainObject(attrs) ? attrs.level : undefined;
      if (level !== 2 && level !== 3 && level !== 4) {
        ctx.addIssue({ code: "custom", message: "Niveau de titre invalide." });
      }
    }

    // paragraph / heading: optional inline content of text nodes.
    if (type === "paragraph" || type === "heading") {
      const content = node.content;
      if (content !== undefined) {
        if (!Array.isArray(content)) {
          ctx.addIssue({ code: "custom", message: "Contenu invalide." });
        } else {
          for (const child of content) stack.push({ node: child, depth: depth + 1, role: "inlineChild" });
        }
      }
      continue;
    }

    if (type === "orderedList" && node.attrs !== undefined && !isPlainObject(node.attrs)) {
      ctx.addIssue({ code: "custom", message: "Attributs invalides." });
    }

    // bulletList / orderedList / listItem: structural containers.
    const content = node.content;
    if (!Array.isArray(content) || (REQUIRES_NON_EMPTY_CONTENT.has(type) && content.length === 0)) {
      ctx.addIssue({ code: "custom", message: "Contenu invalide." });
      continue;
    }

    const childRole: NodeRole = type === "listItem" ? "listItemChild" : "listContainerChild";
    for (const child of content) stack.push({ node: child, depth: depth + 1, role: childRole });
  }

  if (nodeCount > MAX_NODES && !nodesIssued) {
    ctx.addIssue({ code: "custom", message: "Document trop volumineux." });
  }
  if (textLen > MAX_TEXT_LEN) {
    ctx.addIssue({ code: "custom", message: "Texte trop long." });
  }
}) as unknown as z.ZodType<RichTextDoc>;

/** True when the doc has no non-whitespace text (Tiptap's empty state). */
export function isRichTextEmpty(doc: RichTextDoc): boolean {
  return richTextToPlainText(doc).trim().length === 0;
}

/** Wrap plain text into a doc: non-blank lines become paragraphs. */
export function plainTextToRichTextDoc(text: string): RichTextDoc {
  const paras = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .map((l) => ({ type: "paragraph" as const, content: [{ type: "text" as const, text: l }] }));
  return { type: "doc", content: paras.length > 0 ? paras : [{ type: "paragraph" }] };
}

type AnyNode = {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: Array<{ type: string; attrs?: Record<string, unknown> }>;
  content?: AnyNode[];
};

/** Flatten to plain text: block nodes joined by newlines, list items prefixed. */
export function richTextToPlainText(doc: RichTextDoc): string {
  const lines: string[] = [];
  const textOf = (node: AnyNode): string =>
    (node.content ?? []).map((c) => (c.type === "text" ? c.text ?? "" : textOf(c))).join("");
  const walkBlocks = (nodes: AnyNode[], prefix?: (i: number) => string): void => {
    nodes.forEach((node, i) => {
      if (node.type === "paragraph" || node.type === "heading") {
        lines.push((prefix ? prefix(i) : "") + textOf(node));
      } else if (node.type === "bulletList") {
        walkBlocks(node.content ?? [], () => "• ");
      } else if (node.type === "orderedList") {
        let n = 0;
        walkBlocks(node.content ?? [], () => `${(n += 1)}. `);
      } else if (node.type === "listItem") {
        // A listItem gets exactly one number/marker no matter how many
        // line-emitting children it has: apply the prefix to the item's
        // first emitted line only, and render any further children
        // (e.g. a second paragraph, or a nested list) without renumbering.
        const before = lines.length;
        walkBlocks(node.content ?? [], undefined);
        if (prefix && lines.length > before) {
          lines[before] = prefix(i) + lines[before];
        }
      }
    });
  };
  walkBlocks((doc as AnyNode).content ?? []);
  return lines.join("\n");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Pure, XSS-safe HTML. Consumed by sub-project #3 (server PDF). */
export function richTextToHtml(doc: RichTextDoc): string {
  const renderText = (node: AnyNode): string => {
    let html = escapeHtml(node.text ?? "");
    for (const mark of node.marks ?? []) {
      if (mark.type === "bold") html = `<strong>${html}</strong>`;
      else if (mark.type === "italic") html = `<em>${html}</em>`;
      else if (mark.type === "link") {
        const href = typeof mark.attrs?.href === "string" ? mark.attrs.href : "";
        html = isSafeHref(href)
          ? `<a href="${escapeHtml(href)}" rel="noopener noreferrer">${html}</a>`
          : `<span>${html}</span>`;
      }
    }
    return html;
  };
  const inline = (node: AnyNode): string =>
    (node.content ?? []).map((c) => (c.type === "text" ? renderText(c) : "")).join("");
  const block = (node: AnyNode): string => {
    switch (node.type) {
      case "paragraph":
        return `<p>${inline(node)}</p>`;
      case "heading":
        return node.attrs?.level === 4
          ? `<h4>${inline(node)}</h4>`
          : node.attrs?.level === 3
            ? `<h3>${inline(node)}</h3>`
            : `<h2>${inline(node)}</h2>`;
      case "bulletList":
        return `<ul>${(node.content ?? []).map(block).join("")}</ul>`;
      case "orderedList":
        return `<ol>${(node.content ?? []).map(block).join("")}</ol>`;
      case "listItem":
        return `<li>${(node.content ?? []).map(block).join("")}</li>`;
      default:
        return "";
    }
  };
  return ((doc as AnyNode).content ?? []).map(block).join("");
}
